// A `guard-escalated` refusal drawn where the call stopped, for every element
// (02-DESIGN/14-solutions/03-the-console.md § the words; 02-DESIGN/19-chat/
// 01-the-chat-solution.md § an escalated turn; 03-the-guardrails-service.md
// § Approvals; hq decision 0083 § 5, § 6): the crossing read on the person's
// own addresses with the console's own guardrails client and drawn as the
// Approvals card — *Approve and send* where the element asks again itself,
// *Approve* where it cannot — and *Deny*. A crossing addressed to admins that
// the person cannot read says *waiting for an admin*, with the link to it on
// Approvals. While the card is open the crossing is read again every few
// seconds, so a decision taken elsewhere moves it; approved, the element asks
// again once; run out of time, the card says so.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import { Button, NotAllowed, Notice, Pending } from "../../ui/components.tsx";
import { Code } from "../../wire/guard.ts";
import { useSurface } from "../surface.tsx";
import { ended, POLL, polls, reading, resends, step, type Card, type CardEvent } from "./card.ts";
import { getAddressed } from "./getAddressed.ts";
import { RequestCard } from "./RequestCard.tsx";

export function Escalated({
  id,
  resend,
}: {
  /** The crossing's id, from the refusal's Impire-Guard-Escalation header. */
  id: string;
  /** Asks again — the identical request, once — where the element can; absent, the card says to ask again. */
  resend?: () => "sent" | "queued";
}) {
  const s = useSurface();
  const admin = s.offered("approvals:admin");
  const [card, setCard] = useState<Card>(reading);
  const [sending, setSending] = useState<"sent" | "queued" | undefined>(undefined);
  const prev = useRef<Card>(reading);
  const resendRef = useRef(resend);
  useEffect(() => {
    resendRef.current = resend;
  }, [resend]);

  const move = useCallback((ev: CardEvent) => {
    setCard((c) => step(c, ev, Date.now()));
  }, []);

  // Approved, here or elsewhere: the element asks again, once.
  useEffect(() => {
    if (resends(prev.current, card)) {
      setSending(resendRef.current?.());
    }
    prev.current = card;
  }, [card]);

  const read = useCallback(() => {
    getAddressed(s.bus, s.principal, admin, id).then(
      (record) => {
        move({ kind: "read", record });
      },
      (err: unknown) => {
        move(isRefusal(err, Code.notFound) ? { kind: "unreadable" } : { kind: "failed", err });
      },
    );
  }, [s.bus, s.principal, admin, id, move]);

  useEffect(read, [read]);
  const polling = polls(card);
  useEffect(() => {
    if (!polling) {
      return undefined;
    }
    const t = setInterval(read, POLL);
    return () => {
      clearInterval(t);
    };
  }, [polling, read]);

  const link = (
    <Link className="text-link" to={tenantPath(s.tenant, "approvals", id)}>
      Open it on Approvals
    </Link>
  );
  const record =
    card.kind === "reading" || card.kind === "admin" || card.kind === "failed" ? undefined : card.record;
  return (
    <div className="stack escalated">
      {card.kind === "reading" && <Pending what="Reading the request waiting for you" />}
      {card.kind === "admin" && (
        <Notice title="Waiting for an admin">
          A guardrail holds this until an admin of {s.name} approves it. {link}
          {sending === "queued" && " It will send when the current reply ends."}
          {resend !== undefined && sending === undefined && (
            <>
              {" "}
              <Button
                kind="quiet"
                size="sm"
                onClick={() => {
                  setSending(resend());
                }}
              >
                Send again
              </Button>
            </>
          )}
        </Notice>
      )}
      {card.kind === "failed" && (
        <>
          <NotAllowed err={card.err} what="The request waiting for you" />
          <p className="small">{link}</p>
        </>
      )}
      {record !== undefined && (
        <RequestCard
          record={record}
          resend={resend !== undefined}
          quiet
          onResolved={(decision) => {
            move({ kind: "resolved", decision });
          }}
        />
      )}
      {ended(card) && <Outcome card={card} resends={resend !== undefined} sending={sending} />}
    </div>
  );
}

function Outcome({
  card,
  resends,
  sending,
}: {
  card: Card;
  resends: boolean;
  /** What became of the resend: sent, or waiting for the reply in flight to end. */
  sending: "sent" | "queued" | undefined;
}) {
  switch (card.kind) {
    case "granted":
      return resends ? (
        <Notice kind="ok" title="Approved">
          {sending === "queued" ? "Will send when the current reply ends." : "Sending it again."}
        </Notice>
      ) : (
        <Notice kind="ok" title="Approved">
          Ask again, exactly as before; it goes through once.
        </Notice>
      );
    case "denied":
      return <Notice title="Denied">It was not sent. Ask again to open a new request.</Notice>;
    case "expired":
      return <Notice title="Expired">Nobody decided in time. Ask again to open a new request.</Notice>;
    case "withdrawn":
      return <Notice title="Withdrawn">The approval was withdrawn. Ask again to open a new request.</Notice>;
    default:
      return null;
  }
}
